<template>
  <section 
    v-if="!isMobile"
    class="latest-featured dark texture"
    data-color="dark">
    <div 
      class="latest-featured__tiles"
      @mouseover="isInside = true"
      @mouseleave="isInside = false"
      
    >
      <div class="latest-featured__image-container">
        <div class="latest-featured__details">
          Author <span>{{ articles[currentIndex].author }}</span> Tags <span>{{ articles[currentIndex].tags }}</span> Read Time <span>{{ articles[currentIndex].read_time }} minutes</span>
        </div>
        <transition 
          name="slide" 
          mode="out-in"
          @after-leave="onAfterLeave"
          >
          <img
            :key="currentIndex"
            :src="articles[currentIndex].image_src"
            :alt="articles[currentIndex].image_alt"
            class="latest-featured__image latest-featured__image--current"
          />
        </transition>
        <img
          :key="previousIndex"
          :src="articles[previousIndex].image_src"
          :alt="articles[previousIndex].image_alt"
          class="latest-featured__image latest-featured__image--previous"
        />
      </div>
      <div
        v-for="(article, index) in articles"
        :key="index"
        :class="{ 'latest-featured__tile': true, 'latest-featured__tile--hovered': isInside && index !== currentIndex }"
        class="latest-featured__tile"
        @mouseenter="handleMouseOver(index)"
      >
        <div class="latest-featured__header">
          <h3 class="latest-featured__headline">{{ article.post_title }}</h3>
        </div>
        <div class="latest-featured__footer">
          <div class="latest-featured__date">{{ article.date }}</div>
            <div class="latest-featured__button-container">
              <a 
                :href="article.permalink" 
                class="latest-featured__button"
              >
                <magnetic-button 
              :testing=false
              buttonSize="80px"
              magnetPadding="50px"
              buttonText="Read">
              </magnetic-button>
            </a>
            </div>
        </div>
      </div>
    </div>
  </section>

  <section v-else class="latest-featured-mobile dark texture">
    <div class="latest-featured-mobile__feature">
      <div class="latest-featured-mobile__image-container">
        <div v-if="articles.length > 0" class="latest-featured-mobile__details">
          Author <span>{{ articles[0].author }}</span>  Read Time
          <span>{{ articles[0].read_time }} minutes</span>
        </div>
        <img
          v-if="articles.length > 0"
          :src="articles[0].image_src_mobile"
          :alt="articles[0].image_alt_mobile"
          class="latest-featured-mobile__image" />
        </div>
        <div class="latest-featured__highlight-container">
          <h3 class="latest-featured__headline mobile">{{ articles[0].post_title }}</h3>
          <div class="latest-featured__footer">
            <div class="latest-featured__date">{{ articles[0].date }}</div>
              <div class="latest-featured__button-container">
                <a 
                  :href="articles[0].permalink" 
                  class="latest-featured__button"
                >Read</a>
              </div>
          </div>
        </div>
    </div>
    <div class="latest-featured-mobile__additional">
      <!-- Display additional articles in mobile view -->
      <div class="latest-featured-mobile__tile-container">
        <div v-for="(article, index) in articles.slice(1)" :key="index" class="latest-featured-mobile__tile">
          <div class="latest-featured__header">
            <h3 class="latest-featured__headline">{{ article.post_title }}</h3>
          </div>
          <div class="latest-featured__footer">
            <div class="latest-featured__date">{{ article.date }}</div>
            <div class="latest-featured__button-container">
              <a :href="article.permalink" class="latest-featured__button">Read</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { defineProps, ref, onMounted, onBeforeUnmount } from 'vue';
import MagneticButton from '../components/MagneticButton.vue';
import { isMobile } from 'mobile-device-detect';

const props = defineProps({
  articles: Array,
});

const currentIndex = ref(0);
const previousIndex = ref(0);
const isTransitioning = ref(false);
const isInside = ref(false);

const handleMouseOver = (index, event) => {
  isTransitioning.value = true;
  previousIndex.value = currentIndex.value;
  currentIndex.value = index;
};

const onAfterLeave = (el) => {
  isTransitioning.value = false;
  console.log('after leave');
};
</script>

<style lang="scss">
  .latest-featured {
    height: 50vw;
    display: grid;
    grid-template-columns: [full-start] 6.4rem [center-start] 1fr [center-end] 6.4rem [full-end];
    gap: 0;
    padding-left: 0;
    padding-right: 0;        
    &__tiles {
      position: relative;
      grid-column: full-start / center-end;
      display: grid;
      grid-template-columns: 6.4rem repeat(4,calc(25% - 1.6rem));
      grid-template-rows: repeat(2, 50%);
      border-top: 1px solid white;
      border-bottom: 1px solid white;
      border-collapse: collapse;
      overflow: hidden;
    }
    &__image {
      height: 100%;
      width: 100%;
      object-fit: cover;
      position: absolute;
      top: 0;
      left: 0;
      transition: all 0.5s ease-in-out;
      z-index: 1;
      clip-path: inset(0% 0% 0% 0%);
      &--current {
        z-index: 2;
      }
      &-container {
        overflow: hidden;
        position: relative;
        grid-column: 1 / span 3;
        grid-row: 1 / span 2;
      }
    }
    &__tile {
      display: flex;
      justify-content: space-between;
      flex-direction: column;
      color: white;
      padding: 3.2rem;
      border-bottom: 1px solid white;
      border-left: 1px solid white;
      opacity: 1;
      transition: opacity 0.5s ease-in-out;
      overflow: hidden;
      &:last-child {
        border-bottom: none;
      }
      &:nth-child(4){
        border-bottom: none;
      }
      &--hovered {
        .latest-featured__headline,
        .latest-featured__date {
          opacity: 0.3;
        }
      }
      &:hover {
        .latest-featured__button {
          opacity: 1;
          transition-delay: .1s;
        }
      }
    }
    &__details {
      position: absolute;
      left: 9.6rem;
      bottom: 3.2rem;
      font-size: 1.5rem;
      color: white;
      z-index: 3;
      &--mobile {
        left: var(--gutter-mobile-h);
        bottom: var(--gutter-mobile-h);
      }
      span {
        font-family: var(--fontSerifIt);
        margin-right: 1rem;
        font-size: 1.6rem;
      }
    }
    &__headline {
      font-size: 9vw;
      font-size: clamp(2.8rem, 3.5vw, 4rem);
      font-family: var(--fontSerifRg);
      text-transform: uppercase;
      transition: opacity 0.5s ease-in-out;
      padding: var(--gutter-mobile-h) var(--gutter-mobile-h) var(--gutter-mobile-h) 3rem;
      &.mobile {
        font-size: 4rem;
      }
      @media only screen and (min-width: 1025px) {
        padding: 0;
        &.mobile {
          font-size: clamp(2.8rem, 3.5vw, 4rem);
        }
      }
    }
    &__footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--gutter-mobile-h);
      @media only screen and (min-width: 1025px) {
        padding: 0;
      }
    }
    &__button {
      position: absolute;
      width: 8rem;
      height: 8rem;
      font-family: var(--fontSerifIt);
      text-transform: uppercase;
      font-size: 1.8rem;
      display: flex;
      justify-content: center;
      align-items: center;
      color: white;
      border: 1px solid white;
      border-radius: 50%;
      @media only screen and (min-width: 1025px) {
        transition: opacity 0.5s ease-in-out;
        opacity: 0;
        border: none;
      }
      &-container {
        position: relative;
        width: 8rem;
        height: 8rem;
      }
    }
    &__date {
      font-size: 1.3rem;
      color: var(--white);
      text-transform: uppercase;
      font-family: var(--fontSansRm);
      transition: opacity 0.5s ease-in-out;
      @media only screen and (min-width: 1025px) {
        font-size: 1vw;
      }
    }

    &-mobile {
      position: relative;
      &__feature {
        padding-bottom: 1rem;
        .latest-featured__footer {
          padding-left: 3rem;
        }
      }
      &__image-container {
        position: relative;
        margin: 3.2rem var(  --gutter-mobile-h) 0;
      }
      &__details {
        position: absolute;
        left: var(--gutter-mobile-h);
        bottom: var(--gutter-mobile-h);
        font-size: 1.5rem;
        color: white;
        z-index: 3;
        span {
          font-family: var(--fontSerifIt);
          margin-right: 1rem;
          font-size: 1.6rem;
        }
      }
      &__additional {
        overflow-x: scroll;   
        border-top: 1px solid white;
        -webkit-overflow-scrolling: touch;
        transform: translate3d(0, 0, 0);
        &::-webkit-scrollbar {
          display: none;
        }      
      }
      &__tile {
        width: 80vw;
        height: 76vw;
        padding: 2rem 0 1rem;
        border-right: 1px solid white;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        .latest-featured__footer {
          padding-left: 3rem;
        }
        &:last-child {
          border-right: none;
        }
        &-container {
          display: flex;
          flex-direction: row;
          width: 240vw;
        }
      }
      .latest-featured__button {
        opacity: 1;
      }
    }
    &__highlight-container {
      height: 70vw;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
  }
  
.slide-enter-active, .slide-leave-active {
  transition: transform 0s ease;
  transition: all 0s ease;
}

.slide-enter, 
.slide-enter-to,
.slide-leave-to /* .slide-leave-active in <2.1.8 */ {
  clip-path: inset(0% 0% 0% 0%);
}

.slide-leave-to,
.slide-leave, 
.slide-enter-active /* .slide-enter-active in <2.1.8 */ {
  clip-path: inset(0% 100% 0% 0%);
}

.slide-enter-active /* .slide-enter-active in <2.1.8 */ {
  transition: all 0s ease;
}
</style>